import { LoadingOutlined, PlusOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Row,
  Select,
  Spin,
  Switch,
  TreeSelect,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GetActiveScalesApi } from "../../api/GetActiveScalesApi";
import { GetAllPlanGroupsApi } from "../../api/GetAllPlanGroups";
import GetScaleUnitsByScaleApi from "../../api/GetScaleUnitsByScaleApi";
import GetPlanTemplateByIdApi from "../../api/plan-templates/GetPlanTemplateByIdApi";
import CreateSplitPlanApi from "../../api/plans/CreateSplitPlanApi";
import { GetUnitPositionsApi2 } from "../../api/units/GetUnitPositionsApi2";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { GetTreeSelectUnitsApi } from "../../api/GetTreeSelectUnitsApi";


const { Option } = Select;

const { TextArea } = Input;

function SplitPlanModal(props) {
  const [submit, setSubmit] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [planLevels, setPlanLevels] = useState([]);
  const [planLevel, setPlanLevel] = useState([]);
  const [allUnitsTree, setAllUnitsTree] = useState([]);
  const [planGroups, setPlanGroups] = useState([]);
  const [scales, setScales] = useState([]);
  const [scaleUnits, setScaleUnits] = useState([]);
  const [percentage, setPercentage] = useState(false);
  const [addScaleUnit, setAddScaleUnit] = useState(false);
  const [isPercentageUpdate, setIsPercentageUpdate] = useState(false);
  const [isNegativeScale, setIsNegativeScale] = useState(false);
  const [supportsNegativeScale, setSupportsNegativeScale] = useState(true); // Track if current scale supports negative scale
  const [unitPositions, setUnitPositions] = useState([]);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const [form] = Form.useForm();

  function handleCancel() {
    setSubmit(false);
    setAllUnitsTree([]);
    setPlanGroups([]);
    setPlanLevels([]);
    setPlanLevel([]);
    setScales([]);
    setScaleUnits([]);
    setUnitPositions([]);
    setPercentage(false);
    setAddScaleUnit(false);
    setIsPercentageUpdate(false);
    setIsNegativeScale(false);
    setSupportsNegativeScale(true);
    form.resetFields();
    props.handleCancel();
  }

  useEffect(() => {
    if (props.visible) {
      getPlanGroups();
      getActiveScales();
      getPlanLevels();
      getUnitUsers();
      getAllUnitsTree();
      fetchStrategies();
      
      // Robust boolean conversion for isPercentageUpdate
      const isPercentageUpdateValue = Boolean(
        props.plan?.isPercentageUpdate === true || 
        props.plan?.isPercentageUpdate === 1 || 
        props.plan?.isPercentageUpdate === "1" ||
        props.plan?.isPercentageUpdate === "true"
      );
      
      // Robust boolean conversion for isNegativeScale
      const isNegativeScaleValue = Boolean(
        props.plan?.isNegativeScale === true || 
        props.plan?.isNegativeScale === 1 || 
        props.plan?.isNegativeScale === "1" ||
        props.plan?.isNegativeScale === "true"
      );
      
      // Set initial states from parent plan
      setIsPercentageUpdate(isPercentageUpdateValue);
      setIsNegativeScale(isNegativeScaleValue);
    }
  }, [props.visible]);

  async function getUnitUsers() {
    const apiResponse = await GetUnitPositionsApi2(props.plan?.unit?.unitId);

    if (apiResponse.message === "success") {
      setUnitPositions(apiResponse.data);
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    let attributesValues = [];
    planLevel?.planLevelAttributes?.forEach((attribute) => {
      let attributeValue = values[attribute.id];
      
      // Handle dropdown values - skip empty arrays
      if (attribute.valueType === "dropdown") {
        if (Array.isArray(attributeValue) && attributeValue.length === 0) {
          // Skip empty dropdown selections
          return;
        }
      }
      
      // Skip undefined or null values for optional attributes
      if (attributeValue === undefined || attributeValue === null || attributeValue === "") {
        return;
      }
      
      attributesValues.push({
        attributeId: attribute.id,
        value: attributeValue,
      });
    });

    const apiResponse = await CreateSplitPlanApi(
      props.plan?.planId,
      values.targetScale || null,
      addScaleUnit ? values.unitScale || null : null,
      values.title,
      values.planLevel,
      values.description || null,
      values.target || 0,
      values.targetDate || null,
      values.assignTo,
      attributesValues,
      values.unit,
      values.strategyId || null,
      values.cascadeFromSplitPlans || false,
      isPercentageUpdate,
      isNegativeScale
    );
    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.message === "success") {
      openNotificationSuccess("bottomRight", `Split plan successfully`);
      props.handleOk(apiResponse.data);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't split plan. Try again later."
      );
    }
    handleCancel();
  }

  async function getPlanGroups() {
    const apiResponse = await GetAllPlanGroupsApi();
    if (apiResponse.message === "success") {
      setPlanGroups(apiResponse.planGroups);
    }
  }

  async function getPlanLevels() {
    let newPlanLevels = [];
    const apiResponse = await GetPlanTemplateByIdApi(perfCycleState?.template);

    if (apiResponse.success) {
      apiResponse.data?.planLevels?.forEach((level) => {
        if (props.plan.planLevelOrder < level.order) {
          newPlanLevels.push(level);
        }
      });
      setPlanLevels(newPlanLevels);
    }
  }

  async function fetchStrategies() {
    try {
      const token = 'Bearer ' + localStorage.getItem('authToken');
      // Fetch strategies for the specific plan being viewed
      const planId = props.plan?.planId || props.plan?.id;
      if (!planId) {
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}v1/strategies/plan/${planId}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setStrategies(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  }

  async function getActiveScales() {
    const apiResponse = await GetActiveScalesApi();
    if (apiResponse.success) {
      setScales(apiResponse.data);
    }
  }

  function selectPlanGroup(value) {
    planGroups.forEach((group) => {
      if (value === group.id) {
        form.setFields([
          {
            name: "targetScale",
            value: group.scale?.id,
          },
        ]);
        selectScale(group.scale?.id);
      }
    });
  }

  async function selectScale(value) {
    setScaleUnits([]);
    let addUnit = "";
    scales.forEach((scale) => {
      if (scale.id === value) {
        if (scale.name === "Number") {
          setAddScaleUnit(false);
          setPercentage(false);
          setSupportsNegativeScale(true); // Number supports negative scale
        } else if (scale.name === "Percentage") {
          setAddScaleUnit(false);
          setPercentage(true);
          setSupportsNegativeScale(true); // Percentage supports negative scale
        } else if (scale.name === "Currency") {
          setAddScaleUnit(true);
          setPercentage(false);
          setSupportsNegativeScale(true); // Currency supports negative scale
          addUnit = "add";
        } else {
          setAddScaleUnit(true);
          setPercentage(false);
          setSupportsNegativeScale(false); // Other scales don't support negative scale
          // If current scale doesn't support negative scale, disable it
          if (isNegativeScale) {
            setIsNegativeScale(false);
          }
          addUnit = "add";
        }
      }
    });
    if (addUnit === "add") {
      const apiResponse = await GetScaleUnitsByScaleApi(value);
      if (apiResponse.message === "success") {
        setScaleUnits(apiResponse.scaleUnits);
      }
      apiResponse.scaleUnits?.forEach((unit) => {
        if (unit.default) {
          form.setFields([
            {
              name: "unitScale",
              value: unit.id,
            },
          ]);
        }
      });
    }
  }

  async function selectPlanLevel(value) {
    planLevels?.forEach((level) => {
      if (level.id === value) {
        setPlanLevel(level);
      }
    });
  }


  async function getAllUnitsTree() {
    const apiResponse = await GetTreeSelectUnitsApi();
    if (apiResponse.message === "success") {
      setAllUnitsTree(apiResponse.units);
    }
  }
  async function selectUnit(value) {
    const apiResponse = await GetUnitPositionsApi2(value);
    if (apiResponse.success) {
      setUnitPositions(apiResponse.data);
      form.setFields([
        {
          name: "assignTo",
          value: "",
        },
      ]);
    }
  }

  const unitSelector = (
    <Form.Item name="unitScale" noStyle>
      <Select style={{ width: 100 }}>
        {scaleUnits.map((unit) => {
          return (
            <Option key={unit.id} value={unit.id}>
              {unit.abbreviation}
            </Option>
          );
        })}
      </Select>
    </Form.Item>
  );

  function renderDropdownAndBooleanAttributes() {
    let output = [];

    const dropdownAndBooleanAttributes = planLevel?.planLevelAttributes?.filter((attribute) => {
      return attribute.valueType === "dropdown" || attribute.valueType === "boolean";
    }) || [];

    dropdownAndBooleanAttributes.forEach((attribute) => {
      if (attribute.valueType === "dropdown") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item label={attribute.name} name={attribute.id}>
              <Select
                mode="multiple"
                showSearch
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%" }}
                getPopupContainer={(trigger) => trigger.parentNode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {attribute.dropdownOptions?.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        );
      } else if (attribute.valueType === "boolean") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item 
              label={attribute.name} 
              name={attribute.id}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Col>
        );
      }
    });

    return output;
  }

  function renderOtherAttributes() {
    let output = [];

    const otherAttributes = planLevel?.planLevelAttributes?.filter((attribute) => {
      return !(attribute.valueType === "dropdown" || attribute.valueType === "boolean");
    }) || [];

    otherAttributes.forEach((attribute) => {
      if (attribute.valueType === "text" || attribute.valueType === "number" || attribute.valueType === "double" || attribute.valueType === "date") {
        output.push(
          <Col span={12} key={attribute.id}>
            <Form.Item 
              label={attribute.name} 
              name={attribute.id}
              rules={[
                {
                  validator: (_, value) => {
                    if (attribute.valueType === "double" && value !== undefined && value !== null) {
                      if (attribute.minValue !== null && value < attribute.minValue) {
                        return Promise.reject(`Minimum value is ${attribute.minValue}`);
                      }
                      if (attribute.maxValue !== null && value > attribute.maxValue) {
                        return Promise.reject(`Maximum value is ${attribute.maxValue}`);
                      }
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              {attribute.valueType === "text" ? (
                <Input style={{ width: "100%" }} placeholder={attribute.name} />
              ) : attribute.valueType === "number" ? (
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  placeholder={attribute.name}
                />
              ) : attribute.valueType === "double" ? (
                <InputNumber
                  min={attribute.minValue || 0}
                  max={attribute.maxValue || undefined}
                  step={1 / Math.pow(10, attribute.decimalPrecision || 2)}
                  precision={attribute.decimalPrecision || 2}
                  style={{ width: "100%" }}
                  placeholder={attribute.name}
                  formatter={(value) => {
                    if (!value) return '';
                    const precision = attribute.decimalPrecision || 2;
                    return parseFloat(value).toFixed(precision);
                  }}
                  parser={(value) => {
                    if (!value) return '';
                    return parseFloat(value.replace(/[^\d.-]/g, ''));
                  }}
                />
              ) : attribute.valueType === "date" ? (
                <DatePicker style={{ width: "100%" }} placeholder={attribute.name} />
              ) : null}
            </Form.Item>
          </Col>
        );
      }
    });

    return output;
  }

  return (
    <>
      <Modal
        title={`Split Plan`}
        open={props.visible}
        onCancel={() => handleCancel()}
        footer={null}
        destroyOnClose={true}
        width={900}
      >
        <div className="mb-2 text-base">{`Split from: ${props?.plan?.title}`}</div>
        <Form
          name="splitPlan"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          form={form}
          initialValues={{ planGroup: props.plan?.planGroup }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Plan Group*" name="planGroup">
                <Input disabled placeholder="Plan group" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Plan Level"
                name="planLevel"
                rules={[
                  {
                    required: true,
                    message: `Please select plan level!`,
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select plan level"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                  onSelect={(option) => selectPlanLevel(option)}
                >
                  {planLevels?.map((level) => {
                    return (
                      <Option key={level.id} value={level.id}>
                        {level.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Unit*"
                name="unit"
                rules={[
                  {
                    required: true,
                    message: "Please select a unit!",
                  },
                ]}
              >
                <TreeSelect
                  showSearch
                  treeLine={true}
                  dropdownStyle={{
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                  treeData={allUnitsTree}
                  placeholder="Select unit"
                  treeDefaultExpandAll
                  treeNodeFilterProp={"title"}
                  showIcon={false}
                  filterTreeNode
                  onChange={(value) => selectUnit(value)}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Assign to*"
                name="assignTo"
                rules={[
                  {
                    required: true,
                    message: "Please select a position!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select position"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {unitPositions.map((position) => {
                    return (
                      <Option key={position.id} value={position.id}>
                        {(position.user?.firstName
                          ? position.user?.firstName + ", "
                          : "") + position.designation?.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={`Title*`}
                name="title"
                rules={[
                  {
                    required: true,
                    message: `Please enter title!`,
                  },
                ]}
              >
                <Input placeholder={"title"} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Strategy"
                name="strategyId"
              >
                <Select
                  showSearch
                  placeholder="Select strategy (optional)"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  allowClear
                >
                  {strategies.map((strategy) => (
                    <Option key={strategy.id} value={strategy.id}>
                      {strategy.name} {strategy.plan?.title && `(${strategy.plan.title})`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>


            {planLevel.target && (
              <>
                <Col span={12}>
                  <Form.Item
                    label="Target scale*"
                    name="targetScale"
                    rules={[
                      {
                        required: true,
                        message: "Please select a target scale!",
                      },
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Select a scale"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      filterSort={(optionA, optionB) =>
                        optionA.children
                          .toLowerCase()
                          .localeCompare(optionB.children.toLowerCase())
                      }
                      onChange={(value) => selectScale(value)}
                    >
                      {scales.map((scale) => {
                        return (
                          <Option key={scale.id} value={scale.id}>
                            {scale.name}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Target*"
                    name="target"
                    rules={[
                      {
                        required: true,
                        message: "Please enter target!",
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      addonAfter={
                        addScaleUnit ? unitSelector : percentage && "%"
                      }
                      style={{ width: "100%" }}
                      formatter={(value) => {
                        if (!value) return '';
                        // Format number with commas for better readability
                        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                      }}
                      parser={(value) => {
                        if (!value) return '';
                        // Remove commas when parsing
                        return value.replace(/,/g, '');
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item label=" " name="isPercentageUpdate" valuePropName="checked">
                    <div className="flex items-center">
                      <Checkbox
                        checked={isPercentageUpdate}
                        disabled={isNegativeScale} // Disable when negative scale is enabled
                        onChange={(e) => setIsPercentageUpdate(e.target.checked)}
                      >
                        Update by %
                      </Checkbox>

                      <Popover
                        placement="topRight"
                        content={
                          <div className="text-xs">When enabled, progress updates will be entered as percentages (0-100%)</div>
                        }
                        trigger="hover"
                      >
                        <div className="text-gray-500 text-xs ml-1">
                          <InfoCircleOutlined />
                        </div>
                      </Popover>
                    </div>
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item label=" " name="isNegativeScale" valuePropName="checked">
                    <div className="flex items-center">
                      <Checkbox
                        disabled={isPercentageUpdate || !supportsNegativeScale} // Disable when percentage update is enabled or scale doesn't support it
                        onChange={(e) => setIsNegativeScale(e.target.checked)}
                      >
                        Negative Scale
                      </Checkbox>

                      <Popover
                        placement="topRight"
                        content={
                          <div className="text-xs">
                            Enables countdown logic for loss reduction, debt paydown, and cost cutting scenarios. 
                            Progress shows remaining amount instead of achieved amount.
                            <br /><br />
                            <strong>Available for:</strong> Number, Currency, and Percentage scales only.
                          </div>
                        }
                        trigger="hover"
                      >
                        <div className="text-gray-500 text-xs ml-1">
                          <InfoCircleOutlined />
                        </div>
                      </Popover>
                    </div>
                  </Form.Item>
                </Col>
              </>
            )}

            {planLevel.description && (
              <Col span={24}>
                <Form.Item label="Description" name="description">
                  <TextArea
                    showCount
                    maxLength={100}
                    placeholder="Description"
                  />
                </Form.Item>
              </Col>
            )}

            {planLevel.targetDate && (
              <Col span={12}>
                <Form.Item
                  label="Target date*"
                  name="targetDate"
                  rules={[
                    {
                      required: true,
                      message: "Please select a target date!",
                    },
                  ]}
                >
                  <DatePicker className="w-full" placeholder="Target date" />
                </Form.Item>
              </Col>
            )}

            {Boolean(planLevel?.cascadeFromSplitPlansEnabled) && (
              <Col span={24}>
                <Form.Item 
                  label="Cascade from Split Plans"
                  name="cascadeFromSplitPlans"
                  tooltip="When enabled, split plans below this plan will roll up their progress. Individual progress on this plan will be ignored and update button will be disabled."
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch />
                </Form.Item>
              </Col>
            )}

            {renderDropdownAndBooleanAttributes()}
            {renderOtherAttributes()}



            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className=" flex justify-end">
                  <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                    Cancel
                  </Button>
                  <Button
                    className="btn w-32"
                    type="primary"
                    htmlType={submit && "submit"}
                    disabled={submit}
                  >
                    {submit ? (
                      <Spin
                        size="small"
                        indicator={
                          <LoadingOutlined style={{ color: "#fff" }} spin />
                        }
                      />
                    ) : (
                      `Split`
                    )}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default SplitPlanModal;
